<template>
  <div class="sf-overview-navigation" :class="outerClass">
    <div v-if="!isEmpty && !isLoading">
      <TreeMenu
        v-if="overviewTree"
        :items="overviewTree"
        :expanded-items="expandedOverviewGroups"
        :seemless="isTreeMenuSeemless"
        :variant="variant"
        :ctrl-click="ctrlClick"
        :is-dimmed="isDimmed"
        :get-item-data-attrs="getItemDataAttrs"
        @item-clicked="treeMenuItemClicked"
        @item-toggled="toggleOverviewGroup"
        @item-expanded="expandOverviewGroup"
      >
        <template #accent="{ itemForSlot }">
          <slot name="accent" :item-for-slot="itemForSlot" />
        </template>
        <template #itemSlot="{ itemForSlot }">
          <slot name="itemSlot" :item-for-slot="itemForSlot" />
        </template>
      </TreeMenu>
    </div>
    <TreeMenuLoadingState
      v-if="isLoading"
      :level-one-count="6"
      :level-two-count="3"
      :level-three-count="3"
    ></TreeMenuLoadingState>
    <EmptyState
      v-if="!isLoading && isEmpty"
      :title="I18n.t('workflow_review.empty_state.sidebar_no_matches_filter')"
      image-src="empty_states/client-file.svg"
      :max-image-width="isUsingNewLayout() ? '240px' : ''"
    ></EmptyState>
  </div>
</template>
<script lang="ts" setup>
import type {
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
} from '@app/application/workflow-overview-navigation/types'

import { computed } from 'vue'

import { isUsingNewLayout } from '@app/adapters/silverfin'
import EmptyState from '@app/shared/vue/components/molecules/empty-state.vue'
import TreeMenu from '@app/shared/vue/components/menu/tree/tree-menu.vue'
import TreeMenuLoadingState from '@app/shared/vue/components/menu/tree/tree-menu-loading-state.vue'
import { getItemDataAttrs } from '../utils'

interface Props {
  overviewTree: Array<WorkflowOverviewItem | WorkflowOverviewGroup>
  expandedOverviewGroups: Record<string, boolean>
  variant?: 'default' | 'dropdown' | 'slim'
  isLoading: boolean
  ctrlClick?: boolean
  isDimmed?: (item: WorkflowOverviewItem | WorkflowOverviewGroup) => boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  isDimmed: () => () => false,
})

const emit =
  defineEmits<{
    itemClicked: [WorkflowOverviewItem | WorkflowOverviewGroup]
    itemExpanded: [number]
    itemToggled: [string]
  }>()

const I18n = window.I18n

const outerClass = computed<string>(() => {
  if (isTreeMenuSeemless.value) return ''
  return 'sf-overview-navigation--dropdown'
})

const isTreeMenuSeemless = computed<boolean>(() => {
  return props.variant === 'default' || props.variant === 'slim'
})

const isEmpty = computed<boolean>(() => {
  return props.overviewTree?.length === 0
})

function expandOverviewGroup(itemId: number) {
  emit('itemExpanded', itemId)
}

function treeMenuItemClicked(
  item: WorkflowOverviewItem | WorkflowOverviewGroup
) {
  emit('itemClicked', item)
}

function toggleOverviewGroup(group: string) {
  emit('itemToggled', group)
}
</script>
<style scoped lang="scss">
.sf-overview-navigation {
  &--dropdown {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.sf-tree-menu-wrapper .sf-tree-menu__item-icon {
  display: inline-block;
  margin-left: 6px;
}
</style>
